/**
 * formatPlan — single source of truth for the session-plan
 * structure rendered by BOTH surfaces that show a saved plan to
 * a user:
 *
 *   1. The in-app Preview toggle (`web/src/components/PlanPreview.tsx`,
 *      consumes `formatPlan` directly with the `PlanEditorValue`
 *      shape).
 *   2. The parent hand-off PDF (`supabase/functions/_shared/
 *      parentHandoff.ts`, `renderPlanSection`, ESLP-125,
 *      consumes `formatPlanResponse` to map the DB's
 *      snake_case JSON blob into `PlanEditorValue` and then
 *      runs `formatPlan`).
 *
 * Before this module: the Preview JSX in `PlanPreview.tsx` and
 * the Edge Function's `renderPlanSection` each hard-coded their
 * own heading hierarchy, paragraph splitting, and activity row
 * shape. Two divergences were filed as issue #499 — e.g. the
 * Preview called the prompt section "What to track" while the
 * PDF called the same data "Data collection"; the Preview
 * prepended `Student: <name>` only when `studentName` was
 * supplied, while the PDF always rendered `<name> · age N`.
 * An SLP tapping Preview then printing to PDF saw two different
 * documents.
 *
 * After this module: both surfaces consume the same
 * `FormattedPlan` shape — the Preview is a thin JSX mapper over
 * it, and the PDF reads the shared formatter via
 * `formatPlanResponse`. The shape contract is now pinned in one
 * place; a rename of a heading or a reorder of a section
 * happens here and both surfaces update.
 *
 * This file is mirrored byte-for-byte in
 * `supabase/functions/_shared/formatPlan.ts` so the Edge
 * Function Deno runtime can consume it without a cross-runtime
 * import. When you change one copy, change the other.
 *
 * The input shape is the existing `PlanEditorValue` (the in-app
 * edit state). The PDF reads a JSON-encoded `PlanResponse` blob
 * with the same conceptual fields (warmup / target_activities /
 * data_collection_prompts / closing / parent_update_prompt);
 * `formatPlanResponse` normalises that blob to
 * `PlanEditorValue` so `formatPlan` is the single renderer.
 */
export interface FormatPlanActivity {
  /** 1-indexed display number (`1. Name (X min)`). */
  number: number;
  name: string;
  duration_min: number;
  cues: string;
  materials: string;
}

export type FormattedPlanSection =
  | { heading: string; paragraphs: string[] }
  | { heading: string; activities: FormatPlanActivity[] }
  | { heading: string; list: string[] };

export interface FormattedPlan {
  /** H1 — the document title. Always present. */
  title: string;
  /**
   * Optional single-line student header (rendered under the H1
   * by the Preview; the PDF renders it via the clinician header
   * rather than here — same data, different position).
   * Null when no student name was supplied.
   */
  studentLine: string | null;
  /** Zero or more typed sections. Section type is the body shape. */
  sections: FormattedPlanSection[];
}

export interface FormatPlanInput {
  warmup: string;
  activities: {
    name: string;
    duration_min: number;
    cues: string;
    materials: string;
  }[];
  prompts: string[];
  closing: string;
  parentUpdate: string;
}

/**
 * Split a free-text field into paragraph chunks by runs of one
 * or more newlines. Empty chunks are dropped (so a trailing
 * newline doesn't render as a blank `<p>`).
 *
 * `PlanEditorValue.warmup` (and `closing`, `parentUpdate`) are
 * `<textarea>`-driven — the SLP may type a single paragraph, or
 * separate paragraphs with blank lines. We treat `\n\n+` as a
 * paragraph break so an "empty line between paragraphs" input
 * still renders as two paragraphs.
 */
function paragraphs(text: string): string[] {
  return text
    .split(/\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * formatPlan — convert an editable `PlanEditorValue` into a
 * stable, typed `FormattedPlan` ready for both JSX and PDF
 * rendering. Pure: same input → same output. No React, no DOM.
 *
 * Section order is fixed:
 *   1. Warm-up            (paragraphs) — omitted if empty
 *   2. Target activities  (activities)  — omitted if empty
 *   3. What to track      (list)        — omitted if empty
 *   4. Closing            (paragraphs) — omitted if empty
 *   5. Parent update      (paragraphs) — omitted if empty
 *
 * Section headings are also fixed here — the in-app Preview and
 * the PDF both render "Warm-up", "Target activities",
 * "What to track", "Closing", "Parent update prompt". When the
 * two surfaces disagree on a heading string, the disagreement is
 * now confined to this file (issue #499).
 */
export function formatPlan(
  plan: FormatPlanInput,
  options?: { studentName?: string | null },
): FormattedPlan {
  const studentLine =
    options?.studentName && options.studentName.trim().length > 0
      ? `Student: ${options.studentName.trim()}`
      : null;

  const sections: FormattedPlanSection[] = [];

  const warmupParagraphs = paragraphs(plan.warmup);
  if (warmupParagraphs.length > 0) {
    sections.push({ heading: 'Warm-up', paragraphs: warmupParagraphs });
  }

  const nonEmptyActivities = plan.activities.filter(
    (a) =>
      a.name.trim().length > 0 ||
      a.cues.trim().length > 0 ||
      a.materials.trim().length > 0,
  );
  if (nonEmptyActivities.length > 0) {
    sections.push({
      heading: 'Target activities',
      activities: nonEmptyActivities.map((a, idx) => ({
        number: idx + 1,
        name: a.name.trim(),
        duration_min: a.duration_min,
        cues: a.cues.trim(),
        materials: a.materials.trim(),
      })),
    });
  }

  const nonEmptyPrompts = plan.prompts
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (nonEmptyPrompts.length > 0) {
    sections.push({
      heading: 'What to track',
      list: nonEmptyPrompts,
    });
  }

  const closingParagraphs = paragraphs(plan.closing);
  if (closingParagraphs.length > 0) {
    sections.push({ heading: 'Closing', paragraphs: closingParagraphs });
  }

  const parentParagraphs = paragraphs(plan.parentUpdate);
  if (parentParagraphs.length > 0) {
    sections.push({
      heading: 'Parent update prompt',
      paragraphs: parentParagraphs,
    });
  }

  return {
    title: 'Session plan',
    studentLine,
    sections,
  };
}

/**
 * formatPlanResponse — adapter that maps the JSON-encoded
 * `PlanResponse` blob (snake_case field names, written by the
 * iOS serializePlan() / web plan-editor save path) into the
 * canonical `PlanEditorValue` and then runs `formatPlan`.
 *
 * Returns `null` when `raw` is empty / not a JSON object so
 * callers can fall back to plain-text rendering or a
 * "(not saved)" placeholder.
 */
export function formatPlanResponse(
  raw: string | null | undefined,
  options?: { studentName?: string | null },
): FormattedPlan | null {
  const text = (raw ?? '').trim();
  if (text.length === 0 || text === '{}') return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;

  // Field presence sniff — only run the JSON branch when at
  // least one of the canonical snake_case keys is present, so
  // we don't accidentally consume a plain-markdown payload.
  const keys = [
    'warmup',
    'target_activities',
    'data_collection_prompts',
    'closing',
    'parent_update_prompt',
  ];
  if (!keys.some((k) => k in obj)) return null;

  const warmup = stringField(obj['warmup']);
  const activities = activityArrayField(obj['target_activities']);
  const prompts = stringArrayField(obj['data_collection_prompts']);
  const closing = stringField(obj['closing']);
  const parentPrompt = stringField(obj['parent_update_prompt']);

  return formatPlan(
    {
      warmup,
      activities,
      prompts,
      closing,
      parentUpdate: parentPrompt,
    },
    options,
  );
}

function stringField(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function stringArrayField(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string');
}

interface RawPlanActivity {
  name?: unknown;
  duration_min?: unknown;
  cues?: unknown;
  materials?: unknown;
}

function activityArrayField(v: unknown): FormatPlanInput['activities'] {
  if (!Array.isArray(v)) return [];
  const out: FormatPlanInput['activities'] = [];
  for (const item of v) {
    if (typeof item !== 'object' || item === null) continue;
    const a = item as RawPlanActivity;
    out.push({
      name: stringField(a.name),
      duration_min:
        typeof a.duration_min === 'number' && Number.isFinite(a.duration_min)
          ? a.duration_min
          : 0,
      cues: stringField(a.cues),
      materials: stringField(a.materials),
    });
  }
  return out;
}
